import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Radar, FolderKanban, Users, IndianRupee,
  FolderArchive, Settings, LogOut, Menu, X, ChevronRight
} from "lucide-react";

const navItems = [
  { path: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/app/tenders", label: "Tender Radar", icon: Radar },
  { path: "/app/projects", label: "Projects", icon: FolderKanban },
  { path: "/app/labour", label: "Labour Manager", icon: Users },
  { path: "/app/finance", label: "Finance", icon: IndianRupee },
  { path: "/app/documents", label: "Documents", icon: FolderArchive },
  { path: "/app/settings", label: "Settings", icon: Settings },
];

export default function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  let user = {};
  try {
    const userRaw = localStorage.getItem("contractor_os_user");
    if (userRaw && userRaw !== "undefined" && userRaw !== "null") {
      user = JSON.parse(userRaw);
    }
  } catch (e) {
    localStorage.removeItem("contractor_os_user");
  }

  const handleLogout = () => {
    localStorage.removeItem("contractor_os_token");
    localStorage.removeItem("contractor_os_user");
    navigate("/");
  };

  const isActive = (path) => location.pathname === path || (path === "/app/dashboard" && location.pathname === "/app/");

  return (
    <div className="min-h-screen bg-[#0A0A08] flex" data-testid="app-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen w-64 bg-[#111110] border-r border-[#27272A] flex flex-col z-50 transition-transform lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-[#27272A] flex items-center justify-between">
          <div className="font-heading text-xl tracking-[3px]">
            CONTRACTOR<span className="text-[#FF5C1A]">OS</span>
          </div>
          <button
            data-testid="close-sidebar-btn"
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-[#888880] hover:text-[#F5F2E8]"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          {navItems.map((item) => (
            <button
              key={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              onClick={() => { navigate(item.path); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition-colors group ${
                isActive(item.path)
                  ? "text-[#FF5C1A] bg-[#FF5C1A]/8 border-r-2 border-[#FF5C1A]"
                  : "text-[#888880] hover:text-[#F5F2E8] hover:bg-[#1A1A18]"
              }`}
            >
              <item.icon size={18} />
              <span className="font-medium">{item.label}</span>
              {isActive(item.path) && <ChevronRight size={14} className="ml-auto" />}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-[#27272A]">
          <div className="px-2 mb-3">
            <div className="text-xs text-[#F5F2E8] font-medium truncate">{user.email || "contractor@demo.com"}</div>
            <div className="text-[10px] text-[#888880] font-mono truncate">{user.company_name || "My Company"}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-2 py-2 text-sm text-[#E74C3C] hover:bg-[#E74C3C]/10 rounded-sm transition-colors"
          >
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-[#0A0A08]/90 backdrop-blur-xl border-b border-[#27272A] px-4 lg:px-8 py-4 flex items-center justify-between">
          <button
            data-testid="open-sidebar-btn"
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-[#888880] hover:text-[#F5F2E8]"
          >
            <Menu size={22} />
          </button>
          <div className="font-heading text-lg tracking-[2px] uppercase text-[#888880]">
            {navItems.find((n) => isActive(n.path))?.label || "Dashboard"}
          </div>
          <div className="font-mono text-[10px] text-[#888880] tracking-widest hidden sm:block">
            {new Date().toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8 animate-in fade-in duration-200">
          {children}
        </main>
      </div>
    </div>
  );
}
